import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAppSelector } from 'hooks';
import {
  disableBiometric,
  enrollBiometric,
  getBiometryLabel,
  isBiometricAvailable,
  isBiometricEnrolled
} from 'services/nativeBiometric';
import { getAuthToken } from 'utils/auth';
import { updateSecureLoginFlag } from 'utils/secureLogin';

/**
 * Offers biometric enrollment after a login, whatever the method (AC2).
 *
 * Shown seconds after the login, not on some later launch. The offer is
 * flagged rather than opened inline only because useNativeLoginDeepLink finishes
 * by hard-reloading the app (to re-init sockets/Redux from the stored token) —
 * a modal opened before that would be destroyed mid-render. The deep-link
 * handler leaves OFFER_KEY behind and this picks it up as the app comes back up,
 * which the player experiences as one continuous login.
 *
 * It waits on playerEmail because the credential is keyed by it. That arrives
 * with the normal profile fetch during member-app init. If a session somehow
 * never loads the profile, the flag survives to a later launch — a fallback,
 * not the intended path.
 *
 * Declining writes enableSecureLogin=false, matching the auth app's
 * handleSkipPasskey, so a player who says no isn't asked again on either
 * surface. They can still turn it on from account settings.
 */
const OFFER_KEY = 'hj_offer_biometric';

export function markBiometricEnrollmentOffer(): void {
  try {
    localStorage.setItem(OFFER_KEY, '1');
  } catch {
    /* noop — the offer is a nicety, settings still works */
  }
}

/**
 * True while a post-login biometric offer is still to come on this device: the
 * one-shot flag is set and nothing has resolved it yet (native only, and never
 * once biometrics are already enrolled — the hook ignores the flag then, so it
 * must not hold anything else up). For surfaces that must not stack another
 * modal over the offer; the one-time multi-run introduction waits on it
 * (EN-10791). A read, not a subscription: callers re-check on their own clock.
 */
export function isBiometricEnrollmentOfferPending(): boolean {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    return localStorage.getItem(OFFER_KEY) === '1' && !isBiometricEnrolled();
  } catch {
    return false;
  }
}

function clearOffer(): void {
  try {
    localStorage.removeItem(OFFER_KEY);
  } catch {
    /* noop */
  }
}

export type BiometricEnrollmentOffer = {
  show: boolean;
  busy: boolean;
  biometryLabel: string;
  /**
   * True for the confirmation beat after a successful enrolment. Separate from
   * `show` because the offer is finished by then — iOS confirms the SCAN, but
   * nothing else tells the player biometric sign-in is now on for their
   * account (design pass, Jason 2026-08-19).
   */
  enrolled: boolean;
  /**
   * True when enrolment did not complete (scan failed, or iOS closed its
   * prompt after two misses). The drawer used to just disappear here, telling
   * the player nothing about whether the feature was now on
   * (Jason, on-device 2026-08-19).
   */
  failed: boolean;
  enroll: () => void;
  skip: () => void;
  retry: () => void;
  dismissConfirmation: () => void;
};

export function useBiometricEnrollmentOffer(): BiometricEnrollmentOffer {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [failed, setFailed] = useState(false);
  const [biometryLabel, setBiometryLabel] = useState('Face ID or your fingerprint');
  const playerEmail = useAppSelector((state) => state.user.playerEmail);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let pending = false;
    try {
      pending = localStorage.getItem(OFFER_KEY) === '1';
    } catch {
      return;
    }
    // Needs an email to key the credential, so wait for the profile rather than
    // burning the one-shot offer on a boot where it isn't loaded yet.
    if (!pending || !playerEmail || isBiometricEnrolled()) return;

    let cancelled = false;
    isBiometricAvailable()
      .then(async (available) => {
        if (cancelled) return;
        if (!available) {
          // No sensor enrolled — don't offer, and don't keep the flag around to
          // re-check on every boot (AC10).
          clearOffer();
          return;
        }
        const label = await getBiometryLabel();
        if (cancelled) return;
        setBiometryLabel(label === 'biometrics' ? 'Face ID or your fingerprint' : label);
        setShow(true);
      })
      .catch((err) => {
        // isBiometricAvailable/getBiometryLabel are documented to swallow their
        // own errors and resolve false/a fallback label — this is a backstop
        // for if that ever changes, not a path exercised today. Without it, a
        // rejection here would be unhandled AND leave the one-shot flag behind
        // with the offer never shown, silently reprobing on every future boot.
        // Treat it the same as "no sensor available" (AC10): this whole offer
        // is best-effort on top of a login that already succeeded, so
        // don't invent a blocking error state over a probe that couldn't run —
        // just stop offering and free the flag.
        if (cancelled) return;
        console.warn('[Biometrics] enrollment-offer availability check failed:', err);
        clearOffer();
      });

    return () => {
      cancelled = true;
    };
  }, [playerEmail]);

  const enroll = useCallback(() => {
    const { refresh_token: refreshToken } = getAuthToken();
    if (!playerEmail || !refreshToken) {
      clearOffer();
      setShow(false);
      // Nothing got turned on, so say that rather than vanishing — same reason
      // as the failure path below.
      setFailed(true);
      return;
    }
    setBusy(true);
    enrollBiometric(playerEmail, refreshToken)
      .then(async (result) => {
        // A cancelled OS prompt leaves the offer consumed but the flag unset,
        // so the player isn't nagged on every launch. Settings remains the way
        // back in.
        if (result.ok) {
          const synced = await updateSecureLoginFlag(true, playerGUID);
          if (!synced) {
            // Don't leave a device credential the account-level flag
            // disagrees with — a later login could otherwise honor a
            // credential the account itself never actually opted into.
            await disableBiometric();
            setFailed(true);
            return;
          }
          setFailed(false);
          setEnrolled(true);
          return;
        }
        // ANY non-success gets the epilogue.
        //
        // The first version skipped it for `cancelled`, reasoning that a
        // deliberate cancel needs no reply. That was the wrong line to draw:
        // when Face ID fails to match, iOS's own dialog offers only "Cancel",
        // so a player who tried and failed reports back as a cancel and got
        // silence — the drawer vanished and the lobby returned with no word on
        // whether biometrics were now on (Jason, on-device 2026-08-19).
        //
        // The right line is INTENT: they tapped Enable. If it did not end up
        // enabled, say so, whatever the reason. Declining the offer outright
        // ("Not now") still gets no epilogue — that path never reaches here.
        setFailed(true);
      })
      .finally(() => {
        clearOffer();
        setBusy(false);
        setShow(false);
      });
  }, [playerEmail, playerGUID]);

  const skip = useCallback(() => {
    clearOffer();
    setShow(false);
    void updateSecureLoginFlag(false, playerGUID, 'decline');
  }, [playerGUID]);

  const dismissConfirmation = useCallback(() => {
    setEnrolled(false);
    setFailed(false);
  }, []);

  return {
    show,
    busy,
    biometryLabel,
    enrolled,
    failed,
    enroll,
    skip,
    /**
     * Retry fires the OS scan again from where the player already is.
     *
     * It used to clear `failed` and re-raise the offer, which sent them back to
     * a card they had already read and agreed to — the second reading of "Sign
     * in with Face ID / here are the benefits" is pure friction when the answer
     * is already yes (Jason, on-device 2026-08-19). So it is literally `enroll`:
     * the soft-landing card stays mounted, goes busy, and the next thing the
     * player sees is the system prompt. From there the card resolves one of two
     * ways — the confirmation on success, or itself again on another miss — and
     * "Not now" is the only other way out.
     */
    retry: enroll,
    dismissConfirmation
  };
}
